import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, MapPin, Mail, ArrowUp } from 'lucide-react';
import { Button } from '../ui/button';
import OMCMarkSquare from '../branding/OMCMarkSquare';
import { IsnBadge, ArkansasBadge } from '../branding/TrustBadges';

export default function Footer() {
  const navigate = useNavigate();

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const goSection = (id) => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 120);
  };

  const NAV = [
    { label: 'Home', action: () => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); } },
    { label: 'About OMC', action: () => navigate('/about') },
    { label: 'Our Team', action: () => navigate('/team') },
    { label: 'Safety', action: () => navigate('/safety') },
    { label: 'Project Map', action: () => goSection('work') },
    { label: 'Where We Work', action: () => goSection('markets') },
    { label: 'Capabilities', action: () => goSection('capabilities') },
    { label: 'Careers', action: () => navigate('/careers') },
    { label: 'Contact', action: () => navigate('/contact') },
    { label: 'Site Index', action: () => { navigate('/site-index'); window.scrollTo({ top: 0, behavior: 'smooth' }); } },
  ];

  return (
    <footer className="relative bg-coal text-bone overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 bg-grain opacity-[0.04] pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 pb-10">
        {/* Centered brand + nav + contact */}
        <div className="flex flex-col items-center text-center">
          {/* Brand lockup */}
          <div className="flex items-center gap-4 mb-5">
            <OMCMarkSquare size={64} />
            <div className="text-left">
              <p className="font-industrial font-bold text-[17px] leading-tight tracking-[0.13em] uppercase text-bone whitespace-nowrap">
                Ozark Mtn. Construction
              </p>
              <p className="font-mono text-[10px] tracking-[0.075em] uppercase mt-1 whitespace-nowrap">
                <span className="text-[hsl(var(--water-blue-2))]">Water</span>
                <span className="text-steel-blue-2/50"> · </span>
                <span className="text-[hsl(var(--industrial-bronze-2))]">Industrial</span>
                <span className="text-steel-blue-2/50"> · </span>
                <span className="text-[hsl(var(--energy-green-2))]">Energy</span>
              </p>
            </div>
          </div>

          <p className="font-display italic text-sm text-bone/75 mb-3">
            &ldquo;Built for what matters.&rdquo;
          </p>
          <p className="text-sm text-steel-blue-2 leading-[1.7] max-w-2xl mx-auto mb-9">
            Since 1999, Ozark Mountain Construction has delivered the reinforced
            concrete foundations and site work supporting elevated water tanks,
            industrial facilities, and energy infrastructure across the country.
          </p>

          {/* Sitemap — centered horizontal nav */}
          <nav
            className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 mb-9 max-w-3xl"
            data-testid="footer-nav"
          >
            {NAV.map((item) => (
              <button
                key={item.label}
                onClick={item.action}
                className="text-sm text-steel-blue-2 hover:text-bone transition-colors"
                data-testid={`footer-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Contact — centered inline row */}
          <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-sm text-steel-blue-2 mb-8">
            <a href="tel:5014507538" className="flex items-center gap-2 hover:text-bone transition-colors group" data-testid="footer-phone">
              <Phone className="w-4 h-4 text-primary group-hover:text-bone transition-colors" />
              (501) 450-7538
            </a>
            <a href="mailto:info@ozarkmtnco.com" className="flex items-center gap-2 hover:text-bone transition-colors group" data-testid="footer-email">
              <Mail className="w-4 h-4 text-primary group-hover:text-bone transition-colors" />
              info@ozarkmtnco.com
            </a>
            <span className="flex items-center gap-2" data-testid="footer-address">
              <MapPin className="w-4 h-4 text-primary" />
              1002 Artis Lane, Conway, Arkansas 72034
            </span>
          </div>

          <Button
            onClick={() => navigate('/contact')}
            className="btn-lift bg-primary hover:bg-[hsl(var(--water-blue-3))] gap-2 rounded-lg px-6"
            data-testid="footer-contact-cta"
          >
            Contact Us
          </Button>
        </div>

        {/* Trust & compliance badges */}
        <div className="mt-16 flex flex-col items-center" data-testid="footer-trust-badges">
          <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-steel-blue-2/70 mb-6">
            Trusted &amp; Compliant
          </p>
          <div className="flex items-center justify-center gap-12 sm:gap-16 text-bone/80">
            <IsnBadge size={82} />
            <ArkansasBadge size={82} />
          </div>
          <p className="mt-8 max-w-md text-center text-xs text-steel-blue-2/75 leading-relaxed" data-testid="footer-accessibility-statement">
            Ozark Mountain Construction is committed to digital accessibility. This website is
            designed and maintained to meet <span className="text-bone/85">WCAG 2.1 AA</span> (ADA)
            standards. To report an accessibility issue, email{' '}
            <a href="mailto:info@ozarkmtnco.com" className="underline hover:text-bone transition-colors">
              info@ozarkmtnco.com
            </a>.
          </p>
        </div>

        {/* Bottom bar */}
        <div className="mt-14 pt-6 border-t border-white/8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <p className="text-xs text-steel-blue-2/80">
              &copy; {new Date().getFullYear()} Ozark Mountain Construction Co. All rights reserved.
            </p>
            <span className="font-mono text-[10px] text-steel-blue-2/70 tracking-[0.22em] uppercase">
              A+ BBB Rated
            </span>
            <span className="font-mono text-[10px] text-steel-blue-2/70 tracking-[0.22em] uppercase">
              Equal Opportunity Employer
            </span>
            <span className="font-mono text-[10px] text-steel-blue-2/70 tracking-[0.22em] uppercase" data-testid="footer-ada-badge">
              WCAG 2.1 AA Accessible
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={scrollToTop}
            className="text-steel-blue-2 hover:text-bone hover:bg-white/5 gap-2"
            data-testid="footer-back-to-top"
          >
            <ArrowUp className="w-4 h-4" />
            Back to top
          </Button>
        </div>
      </div>
    </footer>
  );
}
